import { useEffect, useState } from 'react'

type VirtualElement = {
  getBoundingClientRect: () => DOMRect
}

type CursorAnchor = {
  /**
   * Floating UI virtual element pinned at the latest cursor position. Pass
   * directly (not as a function) to `BaseTooltip.Positioner.anchor` so
   * `useAnchorPositioning` re-registers the position reference on every
   * cursor update — its `useIsoLayoutEffect` keys on the anchor identity,
   * not on a stable callback wrapper.
   */
  virtualElement: VirtualElement | null
  /** Ref-callback that captures the trigger DOM node for listener attachment. */
  triggerRefCallback: (node: HTMLElement | null) => void
}

/**
 * Build a Floating UI virtual element at the given cursor coordinates.
 * Width/height of 0 mirrors what Floating UI's `useClientPoint` produces,
 * so positioner offsets line up with the existing `followCursor` look.
 */
export const buildCursorVirtualElement = (x: number, y: number): VirtualElement => ({
  getBoundingClientRect: () =>
    ({
      width: 0,
      height: 0,
      x,
      y,
      top: y,
      left: x,
      right: x,
      bottom: y,
      toJSON: () => ({}),
    }) as DOMRect,
})

/**
 * Track the pointer position over a trigger element and expose it as a
 * Floating UI virtual element.
 *
 * Why not Base UI's built-in `trackCursorAxis`: its underlying
 * `useClientPoint` integration caches the previous cursor position across
 * opens via an `initialRef` flag that only resets when the floating element
 * unmounts. With `keepMounted` semantics or fast re-hovers the flag stays
 * true, and the first paint of the next open lands at the previous
 * position before the next `pointermove` snaps it to the cursor.
 *
 * This hook bypasses that path: it tracks the pointer itself in a state
 * value, returns a fresh virtual element on each update, and lets
 * `useAnchorPositioning` re-register the position reference on every
 * cursor move. Updates are throttled with `requestAnimationFrame`, so we
 * render at most once per paint regardless of pointer-event frequency.
 */
export const useCursorAnchor = (enabled: boolean): CursorAnchor => {
  const [triggerEl, setTriggerEl] = useState<HTMLElement | null>(null)
  const [virtualElement, setVirtualElement] = useState<VirtualElement | null>(null)

  useEffect(
    function trackPointerPosition() {
      if (!enabled || triggerEl == null) {
        return undefined
      }

      let frame: number | null = null
      let pendingX = 0
      let pendingY = 0

      const flush = () => {
        frame = null
        setVirtualElement(buildCursorVirtualElement(pendingX, pendingY))
      }

      const onPointer = (event: PointerEvent) => {
        pendingX = event.clientX
        pendingY = event.clientY
        frame ??= requestAnimationFrame(flush)
      }

      const onLeave = () => {
        if (frame != null) {
          cancelAnimationFrame(frame)
          frame = null
        }
      }

      triggerEl.addEventListener('pointerenter', onPointer as EventListener)
      triggerEl.addEventListener('pointermove', onPointer as EventListener)
      triggerEl.addEventListener('pointerleave', onLeave as EventListener)

      return () => {
        triggerEl.removeEventListener('pointerenter', onPointer as EventListener)
        triggerEl.removeEventListener('pointermove', onPointer as EventListener)
        triggerEl.removeEventListener('pointerleave', onLeave as EventListener)
        if (frame != null) {
          cancelAnimationFrame(frame)
        }
      }
    },
    [enabled, triggerEl],
  )

  return {
    virtualElement,
    triggerRefCallback: setTriggerEl,
  }
}

export type { CursorAnchor, VirtualElement }
